import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig, ListParamsType, MultipleActionsParams } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import FormatResourceStatus from "../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { partnerApi } from "../../../../../../../../services/Api/Partner/partnerApi";
import { PartnerSummaryApi } from "../../../../../../../../services/Api/Partner/partnerTypes";
import { selectOrganization } from "../../../../../../../../services/organizations/selectors";
import { downloadFile } from "../../../../../../../../utils";
import CreatePayoffModalForm from "../components/CreatePayoffModalForm";
import SendViaEmail from "../components/SendViaEmail";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "PARTNER_PAYOFF";
const resourceType = "PAYOFF";

const List = ({ resource }: ListState) => {
	const { t } = useTranslation("admin");
	const { addFlash, addSuccessFlash } = useFlash();
	const location = useLocation();
	const organization = useSelector(selectOrganization);
	const confirmation = useConfirmation();
	const [items, setItems] = useState<PartnerSummaryApi[]>([]);
	const [summarizeLoading, setSummarizeLoading] = useState(false);
	const [sendLoading, setSendLoading] = useState(false);
	const [closeLoading, setCloseLoading] = useState(false);
	const [createModal, setCreateModal] = useState<boolean>(false);
	const [showModal, setShowModal] = useState<undefined | PartnerSummaryApi>(undefined);
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;

	if (!resource) return null;
	const data = resource.read();

	const handleRefresh = async () => {
		if (config.fetch) {
			const res = await config.fetch({});
			setItems(res);
		}
	};

	const onSummarizeAllHandler = async () => {
		setSummarizeLoading(true);
		try {
			await confirmation({
				title: "Czy na pewno rozliczyć od początku roku",
				message: "Czy na pewno rozliczyć od początku roku ? Spowoduje to nadpisanie otwartych rozliczeń",
			});
			await adminApi.summarizePartnersPayoffs({ all: true });
			await handleRefresh();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setSummarizeLoading(false);
	};
	const onSummarizeHandler = async () => {
		setSummarizeLoading(true);
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.action"),
			});
			await adminApi.summarizePartnersPayoffs();
			await handleRefresh();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setSummarizeLoading(false);
	};

	const onSummarizePayoff = async (id: number) => {
		try {
			await adminApi.summarizePayoff(id);
			await handleRefresh();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onSendMailHandler = async () => {
		setSendLoading(true);
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.action"),
			});
			await adminApi.sendEmailToPartners();
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setSendLoading(false);
	};

	const onCloseHandler = async () => {
		setCloseLoading(true);
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.action"),
			});
			await adminApi.closeLastMonthPayoffs();
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			await handleRefresh();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setCloseLoading(false);
	};

	const onCancelPayPayoff = async (id: number) => {
		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		await adminApi.cancelPayPayoff(id);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const onCancelPayPayoffs = async (
		payoffs: PartnerSummaryApi[],
		listParams?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const paidPayoffs = payoffs.filter((payOff) => payOff.payment_status === "PAID");
		await adminApi.cancelPayPayoffs(
			paidPayoffs.map((payoff) => Number(payoff.id)),
			multipleActionParams,
			areAllItemsSelected
		);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const onClosePayoff = async (id: number) => {
		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		await adminApi.closePayoff(id);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const onClosePayoffs = async (
		payoffs: PartnerSummaryApi[],
		listParams?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const openPayoffs = payoffs.filter((payOff) => payOff.status === "OPEN");
		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		await adminApi.closePayoffs(
			openPayoffs.map((payoff) => Number(payoff.id)),
			multipleActionParams,
			areAllItemsSelected
		);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const drawDate = (item: PartnerSummaryApi) => {
		if (item.date) {
			const month = `${Number(item.date[1]) < 10 ? `0${item.date[1]}` : item.date[1]}`;
			return `${month}.${item.date[0]}`;
		}
		return "";
	};

	let config = {
		fields: [
			{
				id: "partner",
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getListAdminPartnersSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
				name: t("common.word.partner"),
				render: (item: PartnerSummaryApi) => (
					<Link target={"_blank"} to={`/partnership/${item?.partner.id}`}>
						{item?.partner?.name}
					</Link>
				),
			},
			{
				id: "date",
				type: "date",
				name: t("lib:common.word.date"),
				render: (item: PartnerSummaryApi) => <Link to={`/admin/payoff/${item.id}`}>{drawDate(item)}</Link>,
				renderExport: (item: PartnerSummaryApi) => drawDate(item),
			},
			{
				id: "email",
				type: "text",
				name: t("common.word.email", { ns: "lib" }),
				render: (item: PartnerSummaryApi) => item.partner?.company_email,
			},
			{
				id: "email_sent",
				type: "boolean",
				name: t("modules.payoff.field.email_sent.title"),
				render: (item: PartnerSummaryApi) =>
					item.email_sent ? t("common.word.yes", { ns: "lib" }) : t("common.word.no", { ns: "lib" }),
			},
			{
				id: "status",
				name: t("lib:common.word.status"),
				type: "list",
				listOptions: {
					OPEN: t("enums.common.status.OPEN"),
					CLOSED: t("enums.common.status.CLOSED"),
				},
				render: (item: PartnerSummaryApi) => <FormatResourceStatus status={item.status} />,
			},
			{
				id: "provision_value",
				name: t("common.word.provision_value"),
				render: (item: PartnerSummaryApi) => `${FormatMoney(item.provision_value)}`,
			},
		],
		filters: [
			{
				id: "partner_type",
				name: t("common.word.partner"),
				type: "list",
				options: {
					RESELLER: "Reseller",
					KAM: "KAM",
					TOP: "TOP",
					SALE: "Sale",
					CS: "CS",
					CSV: "CSv",
					SALE_PREMIUM: "Sale premium",
					AFFILIATE: "AFFILIATE",
				},
			},
		],
		selectedColumns: ["partner", "date", "email", "email_sent", "status", "provision_value"],
		actions: [
			{
				name: t("common.word.details"),
				link: (item: PartnerSummaryApi) => `${location.pathname}/${item.id}`,
			},
			{
				name: t("common.action.download_pdf"),
				click: async (item: PartnerSummaryApi) => {
					await downloadFile(
						() => partnerApi.getSummaryPdf(item.id),
						`${t("common.word.payoff")}_${item.number}.pdf`,
						addFlash
					);
				},
			},
			{
				name: t("modules.payoff.action.payoff_again.title"),
				click: (item: PartnerSummaryApi) => onSummarizePayoff(item.partner.id),
				refreshListAfter: true,
			},
			{
				name: t("common.action.send_pdf"),
				click: (item: PartnerSummaryApi) => setShowModal(item),
			},
			{
				name: t("modules.payoff.action.cancel_pay.title"),
				click: (item: PartnerSummaryApi) => onCancelPayPayoff(Number(item.id)),
				visible: (item: PartnerSummaryApi) => item.payment_status === "PAID",
				refreshListAfter: true,
			},
			{
				name: t("common.action.close", { ns: "lib" }),
				click: (item: PartnerSummaryApi) => onClosePayoff(Number(item.id)),
				visible: (item: PartnerSummaryApi) => item.status === "OPEN",
				refreshListAfter: true,
			},
		],
		multipleActions: [
			{
				name: t("modules.payoff.action.cancel_pay.title"),
				click: (
					items: PartnerSummaryApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => onCancelPayPayoffs(items, listParams, areAllItemsSelected, multipleActionParams),
				visible: (items: PartnerSummaryApi[]) =>
					items.filter((item) => item.payment_status === "PAID").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.close", { ns: "lib" }),
				click: (
					items: PartnerSummaryApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => onClosePayoffs(items, listParams, areAllItemsSelected, multipleActionParams),
				visible: (items: PartnerSummaryApi[]) => items.filter((item) => item.status === "OPEN").length > 0,
				refreshListAfter: true,
			},
		],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all", { ns: "lib" }),
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			return adminApi.getPartnersPayoffs(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		exportConfig: {
			title: t("modules.payoff.header.title"),
			filename: t("modules.payoff.field.export_config.filename"),
			organization: `${organization.name}`,
		},
		numberOfStickyColumnsAtTheStart: 2,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};
	const buttons: ButtonProps[] = [
		{
			action: () => setCreateModal(true),
			title: "Dodaj rozliczenie",
		},
		{
			action: onSummarizeAllHandler,
			loading: summarizeLoading,
			title: t("modules.payoff.action.payoff_this_year.title"),
		},
		{
			title: t("modules.payoff.action.payoff.title"),
			loading: summarizeLoading,
			variant: "primary",
			action: onSummarizeHandler,
		},
		{
			variant: "primary",
			loading: closeLoading,
			title: t("modules.payoff.action.close.title"),
			action: onCloseHandler,
		},
		{
			variant: "primary",
			loading: sendLoading,
			title: t("modules.payoff.action.send_to_all.title"),
			action: onSendMailHandler,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			action: onSummarizeAllHandler,
			title: t("modules.payoff.action.payoff_this_year.title"),
		},
		{
			title: t("modules.payoff.action.payoff.title"),
			action: onSummarizeHandler,
		},
		{
			title: t("modules.payoff.action.close.title"),
			action: onCloseHandler,
		},
		{
			title: t("modules.payoff.action.send_to_all.title"),
			action: onSendMailHandler,
		},
	];

	return (
		<div className="content">
			{!isMobile && <Header title={t("modules.payoff.header.title")} buttons={buttons} />}
			<ListData
				data={items}
				config={config}
				onFetch={(items: PartnerSummaryApi[]) => setItems(items)}
				mobileActions={mobileActions}
			/>
			{showModal && <SendViaEmail onHide={() => setShowModal(undefined)} summary={showModal} />}
			{createModal && <CreatePayoffModalForm onHide={() => setCreateModal(false)} isShown={true} />}
		</div>
	);
};

const AdminPayoffIndexPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation("admin");

	useEffect(() => {
		handleChangeTabTitle(`${t("modules.payoff.header.title")} | ${t("lib:go_component.navigation.admin")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<List resource={resource} />
		</Suspense>
	);
};
export default AdminPayoffIndexPage;
